import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Star, ArrowRight, Briefcase, Clock, MapPin, ExternalLink,
  Share2, Globe, ShieldCheck, Copy, Check
} from 'lucide-react';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import axios from 'axios';

const useMetaTags = (tags) => {
  React.useEffect(() => {
    if (!tags) return;
    const prev = { title: document.title };
    const created = [];

    if (tags.title) document.title = tags.title;

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        prev[`${attr}:${key}`] = el.getAttribute('content');
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', content);
        document.head.appendChild(el);
        created.push(el);
      }
    };

    Object.entries(tags.og || {}).forEach(([k, v]) => v && setMeta('property', `og:${k}`, v));
    Object.entries(tags.twitter || {}).forEach(([k, v]) => v && setMeta('name', `twitter:${k}`, v));
    if (tags.description) setMeta('name', 'description', tags.description);

    return () => {
      document.title = prev.title;
      created.forEach(el => el.remove());
      Object.entries(prev).forEach(([key, val]) => {
        if (key === 'title') return;
        const [attr, name] = key.split(':').length > 2
          ? [key.split(':')[0], key.split(':').slice(1).join(':')]
          : key.split(':');
        const el = document.querySelector(`meta[${attr}="${name}"]`);
        if (el && val) el.setAttribute('content', val);
      });
    };
  }, [tags]);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicProfile = () => {
  const { userCode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [branding, setBranding] = useState({ logo_url: '/taskpiece-logo.png', site_name: 'TaskPiece' });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/profile/${userCode}`),
      axios.get(`${API}/settings/branding`),
    ])
      .then(([profileRes, brandRes]) => {
        setData(profileRes.data);
        setBranding(brandRes.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [userCode]);

  // Compute meta tag values (safe even when data is null)
  const metaTags = React.useMemo(() => {
    if (!data) return null;
    const u = data.user;
    const profileUrl = window.location.href;
    const title = `${u.full_name} - Freelancer on ${branding.site_name || 'TaskPiece'}`;
    const skills = (u.skills || []).slice(0, 5).join(', ');
    const description = u.bio
      ? `${u.bio.slice(0, 120)}${u.bio.length > 120 ? '...' : ''}`
      : `${u.full_name} is a freelancer on ${branding.site_name || 'TaskPiece'}${skills ? ` skilled in ${skills}` : ''}. ${u.rating ? `Rated ${u.rating.toFixed(1)}/5` : ''} | ${u.completed_jobs || 0} jobs completed.`;
    return {
      title,
      description,
      og: { type: 'profile', title, description, url: profileUrl, site_name: branding.site_name || 'TaskPiece', image: u.avatar_url || '' },
      twitter: { card: 'summary', title, description, image: u.avatar_url || '' },
    };
  }, [data, branding]);

  useMetaTags(metaTags);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <Globe className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-heading font-bold text-primary" data-testid="profile-not-found">Profile Not Found</h2>
            <p className="text-muted-foreground text-sm">This profile link may be invalid or the user no longer exists.</p>
            <Button onClick={() => navigate('/explore')} data-testid="go-explore-btn">Browse Marketplace</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { user, reviews, gigs, portfolio, badges } = data;
  const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-profile-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="font-heading font-bold text-xl text-primary flex items-center gap-2" data-testid="header-logo">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.site_name} className="h-7 max-w-[160px] object-contain" />
            ) : (
              <Logo className="h-7 max-w-[160px] object-contain" />
            )}
          </a>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/explore')} data-testid="header-explore-btn">Explore</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="header-login-btn">Log In</Button>
            <Button size="sm" onClick={() => navigate('/signup')} data-testid="header-signup-btn">Sign Up</Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Profile Hero */}
        <Card className="mb-6 overflow-hidden" data-testid="profile-hero-card">
          <div className="h-28 bg-gradient-to-r from-primary/80 to-primary/40" />
          <CardContent className="relative pt-0 pb-6">
            <div className="flex flex-col sm:flex-row items-start gap-5 -mt-12">
              <div className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-4xl font-heading font-bold border-4 border-white shadow-lg flex-shrink-0" data-testid="profile-avatar">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  user.full_name?.charAt(0)
                )}
              </div>
              <div className="flex-1 pt-2 sm:pt-6">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary" data-testid="profile-name">
                    {user.full_name}
                  </h1>
                  {user.verified && (
                    <ShieldCheck className="w-5 h-5 text-blue-500" data-testid="verified-badge" />
                  )}
                </div>

                {user.user_code && (
                  <code className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded font-mono mb-2 inline-block" data-testid="profile-user-code">
                    {user.user_code}
                  </code>
                )}

                <div className="flex flex-wrap items-center gap-3 mt-2 mb-3">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                    <span className="font-heading font-bold text-sm">{user.rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-xs text-muted-foreground">({user.total_reviews || 0} reviews)</span>
                  </div>
                  <span className="text-slate-300">|</span>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Briefcase className="w-3.5 h-3.5" />
                    <span>{user.completed_jobs || 0} jobs completed</span>
                  </div>
                  {user.location && (
                    <>
                      <span className="text-slate-300">|</span>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{user.location}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Badges */}
                {badges && badges.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3" data-testid="profile-badges">
                    {badges.map((b) => (
                      <Badge
                        key={b.id}
                        style={{ backgroundColor: b.badge_color + '20', color: b.badge_color, borderColor: b.badge_color + '40' }}
                        variant="outline"
                        className="text-xs font-semibold"
                        data-testid={`badge-${b.badge_id}`}
                      >
                        {b.badge_name}
                      </Badge>
                    ))}
                  </div>
                )}

                <p className="text-muted-foreground text-sm leading-relaxed" data-testid="profile-bio">
                  {user.bio || 'Freelancer on TaskPiece'}
                </p>

                {memberSince && (
                  <p className="text-xs text-muted-foreground mt-2">Member since {memberSince}</p>
                )}
              </div>

              {/* Share Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="flex-shrink-0 gap-1.5"
                data-testid="share-profile-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Share'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Skills */}
        {user.skills && user.skills.length > 0 && (
          <Card className="mb-6" data-testid="skills-section">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-3">Skills</h2>
              <div className="flex gap-2 flex-wrap">
                {user.skills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary" data-testid={`skill-${idx}`}>{skill}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hourly Rate */}
        {user.hourly_rate > 0 && (
          <Card className="mb-6" data-testid="rate-section">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hourly Rate</p>
                <p className="font-heading font-bold text-lg text-primary">₹{user.hourly_rate?.toLocaleString('en-IN')}/hr</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Portfolio */}
        {portfolio && portfolio.length > 0 && (
          <Card className="mb-6" data-testid="portfolio-section">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-4">Portfolio ({portfolio.length})</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {portfolio.map((item) => (
                  <div
                    key={item.id}
                    className="group border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    data-testid={`portfolio-item-${item.id}`}
                  >
                    {item.image_url && (
                      <div className="aspect-video bg-slate-100 overflow-hidden">
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                      {item.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                      )}
                      {item.project_url && (
                        <a
                          href={item.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                        >
                          <ExternalLink className="w-3 h-3" /> View Project
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Gigs */}
        {gigs && gigs.length > 0 && (
          <Card className="mb-6" data-testid="gigs-section">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-4">Active Gigs ({gigs.length})</h2>
              <div className="space-y-3">
                {gigs.map((gig) => (
                  <div
                    key={gig.id}
                    className="flex justify-between items-center py-3 px-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/explore/gig/${gig.id}`)}
                    data-testid={`gig-${gig.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{gig.title}</p>
                      <p className="text-xs text-muted-foreground">{gig.category} · {gig.delivery_days}d delivery</p>
                    </div>
                    <span className="font-heading font-bold text-secondary ml-3 flex-shrink-0">₹{gig.price?.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        {reviews && reviews.length > 0 && (
          <Card className="mb-6" data-testid="reviews-section">
            <CardContent className="pt-5">
              <h2 className="font-heading font-bold text-lg mb-4">Reviews ({reviews.length})</h2>
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b pb-3 last:border-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-500 text-amber-500' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      <span className="text-sm font-medium">{r.reviewer_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString('en-IN')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{r.comment}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="text-center py-6" data-testid="hire-cta-section">
          <p className="text-muted-foreground text-sm mb-3">Interested in working with {user.full_name}?</p>
          <Button size="lg" onClick={() => navigate('/signup')} data-testid="hire-signup-btn">
            Sign Up to Hire <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicProfile;
